import { createMeetingSchema } from '@nexo/types';
import {
  Avatar,
  Button,
  cn,
  DatePicker,
  Field,
  Input,
  Modal,
  Select,
  Textarea,
  TimePicker,
} from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Hash, Link2, Volume2 } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { isoDateFromToday } from '../../lib/format';
import { errorMessage, validate } from '../../lib/forms';
import { useMe } from '../auth/AuthProvider';
import { channelLabel } from '../channels/ChannelSidebar';

const pad = (n: number) => String(n).padStart(2, '0');
const localDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Next half hour from now, as local date and time inputs. */
function nextSlot() {
  const d = new Date(Date.now() + 30 * 60_000);
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
  return { date: localDate(d), time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
}

const durations = [15, 30, 45, 60, 90, 120];

/** Schedule a meeting in a conversation (voice channels first). */
export function ScheduleMeetingDialog({
  open,
  onClose,
  channelId: initialChannel,
}: {
  open: boolean;
  onClose: () => void;
  channelId?: string;
}) {
  const { channels, members } = useTeam();
  const me = useMe();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const rooms = [
    ...channels.filter((c) => c.mode === 'voice'),
    ...channels.filter((c) => c.mode === 'text' && c.kind === 'channel'),
    ...channels.filter((c) => c.kind === 'dm'),
  ];
  const slot = nextSlot();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(slot.date);
  const [time, setTime] = useState(slot.time);
  const [duration, setDuration] = useState(30);
  const [channelId, setChannelId] = useState(initialChannel ?? rooms[0]?.id ?? '');
  const [attendees, setAttendees] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [guestLink, setGuestLink] = useState(false);
  const [error, setError] = useState<string>();

  const create = useMutation({
    mutationFn: () =>
      api.meetings.create({
        title,
        description,
        channelId,
        startsAt: new Date(`${date}T${time}`).toISOString(),
        durationMinutes: duration,
        attendeeIds: attendees,
        guestLink,
      }),
    onSuccess: (meeting) => {
      cache.upsertMeeting(queryClient, meeting);
      setTitle('');
      setDescription('');
      setAttendees([]);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const { errors } = validate(createMeetingSchema, {
      title,
      channelId,
      startsAt: new Date(`${date}T${time}`).toISOString(),
    });
    if (errors) return setError(errors.title ?? errors.channelId ?? errors.startsAt);
    create.mutate();
  }

  const others = members.filter((m) => m.id !== me.id);
  const toggle = (id: string) =>
    setAttendees((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Programar reunión')}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('Cancelar')}
          </Button>
          <Button type="submit" form="schedule-meeting" loading={create.isPending}>
            {t('Programar')}
          </Button>
        </>
      }
    >
      <form id="schedule-meeting" onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field label={t('Título')} error={error}>
          {(props) => (
            <Input
              {...props}
              autoFocus
              maxLength={120}
              placeholder={t('Planificación semanal')}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setError(undefined);
              }}
            />
          )}
        </Field>
        <div className="grid grid-cols-[1fr_auto_auto] gap-2">
          <Field label={t('Día')}>
            {(props) => (
              <DatePicker
                {...props}
                min={isoDateFromToday(0)}
                value={date || null}
                onChange={(value) => value && setDate(value)}
              />
            )}
          </Field>
          <Field label={t('Hora')}>
            {(props) => <TimePicker {...props} value={time} onChange={setTime} />}
          </Field>
          <Field label={t('Duración')}>
            {(props) => (
              <Select
                {...props}
                value={String(duration)}
                onChange={(v) => setDuration(Number(v))}
                options={durations.map((d) => ({
                  value: String(d),
                  label: d < 60 ? t('{{n}} min', { n: d }) : t('{{n}} h', { n: d / 60 }),
                }))}
              />
            )}
          </Field>
        </div>
        <Field label={t('Dónde')}>
          {(props) => (
            <Select
              {...props}
              value={channelId}
              onChange={setChannelId}
              options={rooms.map((c) => ({
                value: c.id,
                label: `${c.mode === 'voice' ? '🔊 ' : c.kind === 'dm' ? '@' : '#'}${channelLabel(c)}`,
              }))}
            />
          )}
        </Field>
        <fieldset>
          <legend className="mb-1.5 text-sm font-medium">{t('Invitar')}</legend>
          <div className="flex flex-wrap gap-1.5">
            {others.map((m) => (
              <button
                key={m.id}
                type="button"
                aria-pressed={attendees.includes(m.id)}
                onClick={() => toggle(m.id)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border py-0.5 pr-2.5 pl-0.5 text-sm',
                  attendees.includes(m.id)
                    ? 'border-accent bg-accent-soft text-accent'
                    : 'border-border hover:bg-surface-2',
                )}
              >
                <Avatar id={m.id} name={m.displayName} src={m.avatarUrl} size="xs" />
                {m.displayName}
              </button>
            ))}
            {others.length === 0 && (
              <p className="text-xs text-muted">{t('Todavía no hay más personas en el equipo.')}</p>
            )}
          </div>
        </fieldset>
        <Field label={t('Descripción (opcional)')}>
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              placeholder={t('Orden del día, enlaces…')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
        <label className="flex items-start gap-3 rounded-lg border border-border p-3">
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-[var(--color-accent)]"
            checked={guestLink}
            onChange={(e) => setGuestLink(e.target.checked)}
          />
          <span>
            <span className="flex items-center gap-1.5 text-sm font-medium">
              <Link2 className="size-4" /> {t('Enlace para invitados externos')}
            </span>
            <span className="block text-xs text-muted">
              {t(
                'Personas sin cuenta (clientes, colaboradores) podrán entrar con su nombre desde 15 minutos antes.',
              )}
            </span>
          </span>
        </label>
        <p className="flex items-center gap-1.5 text-xs text-subtle">
          {channels.find((c) => c.id === channelId)?.mode === 'voice' ? (
            <Volume2 className="size-3.5" />
          ) : (
            <Hash className="size-3.5" />
          )}
          {t('La reunión se hace en la llamada de esa conversación.')}
        </p>
      </form>
    </Modal>
  );
}
